import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  ModalOverlay,
  ResponsiveContent,
  ModalCloseHeader
} from '@evdy/web-core/dist/components/shared/Modal2020';
import TextInput from '../TextInput';

import { webCommunityOperations } from '@evdy/web-redux/dist/lib/webCommunity';

import { nameString, formatPhoneNumber } from '@evdy/web-core/dist/lib/utils';

import styles from './PostMessageModal.module.scss';

const { stageMessageValues, leaveMessage } = webCommunityOperations;

const {
  'post-message-modal': postMessageModalClass,
  'post-message-body': postMessageBodyClass,
  inputs: inputsClass,
  'your-mobile': yourMobileClass,
  'not-you-container': notYouContainerClass,
  'button-container': buttonContainerClass
} = styles;

const conditionalField = (condition, value) => (condition ? value : undefined);

const PostMessageModal = ({ baseRoute, closeModal, initialCondolence }) => {
  const { user, memorialId, messageStaging, decedentName } = useSelector(
    ({ authUser, webCommunity }) => ({
      memorialId: webCommunity.inviteMeta.data?._id,
      decedentName: webCommunity.decedentDetails.data?.name,
      user: authUser?.data,
      messageStaging: webCommunity.messageStaging
    })
  );
  const dispatch = useDispatch();

  const isLoggedIn = !!user?._id;
  const userHasPhone = !!user?.phone || !!user?.meta?.posterShellPhone;
  const { messageSaved, loginById } = messageStaging;

  // TODO: see if we can consolidate Yup schema validation somehow post-MVP
  const initialNameValue = user?.name ? nameString(user?.name) : '';
  const initialValues = {
    name: conditionalField(isLoggedIn, initialNameValue),
    message: initialCondolence || ''
  };
  const PostMessageFormSchema = Yup.object().shape({
    name: conditionalField(isLoggedIn, Yup.string().required('Name is required!')),
    message: Yup.string().required('Message is required!')
  });

  const handleNotYou = () => {
    browserHistory.push({ pathname: `${baseRoute}/condolence/create-user` });
  };

  const saveMessage = ({ name, email, phone, message }) => {
    dispatch(
      leaveMessage({
        memorialId,
        name,
        email,
        phone,
        message,
        isLoggedIn
      })
    );
  };

  const onSubmit = ({ name, message }) => {
    if (name) {
      saveMessage({ name, message });
    } else {
      // store message and open anonymous details modal
      dispatch(stageMessageValues({ message }));
      browserHistory.push({ pathname: `${baseRoute}/condolence/guest-details` });
    }
  };

  // if message is sent and user does not have a phone number
  // then open the add phone modal, else finish flow
  useEffect(() => {
    if (messageSaved) {
      if (!userHasPhone) {
        browserHistory.push({ pathname: `${baseRoute}/condolence/add-phone` });
      } else {
        // finish message post flow
        browserHistory.push({ pathname: `${baseRoute}/condolence/message-posted` });
      }
    }
  }, [baseRoute, messageSaved, userHasPhone]);

  return (
    <ModalOverlay modalVisible {...{ closeModal }} customClass={postMessageModalClass}>
      <ResponsiveContent>
        <ModalCloseHeader {...{ closeModal }} />
        <div className={postMessageBodyClass}>
          <h3>Share your memories, condolences & support</h3>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={PostMessageFormSchema}
            onSubmit={onSubmit}
          >
            <Form>
              <div className={inputsClass}>
                {isLoggedIn && (
                  <>
                    {loginById && (
                      <div className={notYouContainerClass}>
                        <div className={yourMobileClass}>
                          <span>Your Mobile: </span>
                          {userHasPhone
                            ? formatPhoneNumber(user?.phone || user?.meta?.posterShellPhone)
                            : '—'}
                        </div>
                        <button onClick={handleNotYou}>Not you?</button>
                      </div>
                    )}
                    <TextInput
                      type="text"
                      label="Posting as"
                      name="name"
                      placeholder="John Smith"
                    />
                  </>
                )}
                <TextInput textarea name="message" placeholder="Write your message here..." />
              </div>
              <div className={buttonContainerClass}>
                <button type="submit">Post</button>
              </div>
            </Form>
          </Formik>
        </div>
      </ResponsiveContent>
    </ModalOverlay>
  );
};

export default PostMessageModal;
